import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/sections/Footer';
import { QuestionnaireForm, carryShared } from '../components/quote/QuestionnaireForm';
import { FormField } from '../components/quote/FormField';
import { useText } from '../lib/siteContent';
import { DEVIS_FORMS, DEVIS_UNIVERSES, QUESTIONNAIRES } from '../lib/questionnaires';
import { applyOverrides, useQuestionnaireOverrides } from '../lib/questionnaireOverrides';

const Layout = ({ children, testId }) => (
  <div className="min-h-screen bg-[#FFFFFF] text-[#111111]" data-testid={testId}>
    <Navbar />
    <main className="container-custom pt-16 md:pt-24 lg:pt-32 pb-32">{children}</main>
    <Footer />
  </div>
);

export default function DevisPage() {
  const t = useText();
  return (
    <Layout testId="devis-page">
      <div className="grid grid-cols-1 lg:grid-cols-[5fr_6fr] gap-16 lg:gap-20 xl:gap-28">
        <div className="lg:sticky lg:top-32 self-start">
          <h1 className="font-serif font-light text-[#111111] text-5xl sm:text-6xl xl:text-7xl leading-[1.04]" data-testid="devis-title">
            <span className="whitespace-pre-line">{t('devis.headline')}</span>
          </h1>
          <p className="mt-10 text-sm md:text-base leading-[1.9] font-light text-[#333333] max-w-md" data-testid="devis-intro">
            {t('devis.intro')}
          </p>
        </div>
        <DynamicDevis />
      </div>
    </Layout>
  );
}

const UNIVERSE_FIELD = { type: 'universe', label: 'Quel univers souhaitez-vous nous confier ?', options: DEVIS_UNIVERSES.map((u) => u.label), required: true, tilesClass: 'grid grid-cols-1 sm:grid-cols-3 gap-3' };

function DynamicDevis() {
  const [sel, setSel] = useState(null);
  const [done, setDone] = useState(false);
  const store = useRef({});
  const pointer = useRef(false);
  const formRef = useRef(null);
  const ov = useQuestionnaireOverrides();
  const form = (k) => applyOverrides(DEVIS_FORMS[k], ov?.[k]);
  // Mobile/tablet only, pointer selections only: bring the questionnaire start into view
  useEffect(() => {
    if (!sel || !pointer.current || !window.matchMedia('(max-width: 1023px)').matches) return;
    pointer.current = false;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    formRef.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }, [sel]);
  const choose = (label) => {
    const key = DEVIS_UNIVERSES.find((u) => u.label === label).key;
    if (key === sel) return;
    if (sel) store.current[key] = carryShared(form(sel), store.current[sel] || {}, form(key), store.current[key] || {});
    setSel(key);
  };
  return (
    <div data-testid="devis-dynamic">
      {!done && (
        <div className="mb-16" onPointerDown={() => { pointer.current = true; }} onKeyDown={() => { pointer.current = false; }}>
          <FormField id="devis-universe" f={UNIVERSE_FIELD} value={DEVIS_UNIVERSES.find((u) => u.key === sel)?.label} onChange={choose} />
          {!sel && <p className="mt-6 text-xs font-light text-[#6B6B6B]" data-testid="devis-universe-hint">Sélectionnez un univers pour afficher le questionnaire correspondant.</p>}
        </div>
      )}
      {sel && ov && (
        <div ref={formRef} data-testid="devis-questionnaire">
        <QuestionnaireForm key={sel} def={form(sel)} initialValues={store.current[sel]}
          onValuesChange={(v) => { store.current[sel] = v; }} onSuccess={() => setDone(true)} />
        </div>
      )}
    </div>
  );
}

export function QuestionnairePage() {
  const { slug } = useParams();
  const ov = useQuestionnaireOverrides();
  if (!QUESTIONNAIRES[slug]) return <Navigate to="/devis" replace />;
  const def = applyOverrides(QUESTIONNAIRES[slug], ov?.[slug]);
  return (
    <Layout testId={`questionnaire-page-${slug}`}>
      {ov && <div className="max-w-3xl">
        <span className="block text-[11px] uppercase tracking-[0.3em] text-[#6B6B6B] mb-8">Questionnaire détaillé</span>
        <h1 className="font-serif font-light text-[#111111] text-5xl sm:text-6xl leading-[1.04]" data-testid="questionnaire-title">{def.title}</h1>
        <p className="mt-10 mb-20 text-sm md:text-base leading-[1.9] font-light text-[#333333] whitespace-pre-line">{def.intro}</p>
        <QuestionnaireForm def={def} showHeader={false} />
      </div>}
    </Layout>
  );
}
